<template>
  <div>
    <van-popup
      v-model="videoVisible"
      :overlay="!videoFullscreen"
      :overlay-style="videoOverlayStyle"
      :close-on-click-overlay="!videoFullscreen"
      :style="videoPopupStyle"
      round
      class="course-material-video-popup"
      :class="{ 'course-material-video-popup-fullscreen': videoFullscreen }"
      @opened="updateVideoPopupBoundary"
      @closed="handleVideoClosed"
    >
      <training-camp-video-player
        v-if="playerVideo"
        ref="mediaVideoPlayer"
        :playerVideo="playerVideo"
        :enable-trace="videoEnableTrace"
        @fullscreen-change="handleVideoFullscreenChange"
      />
    </van-popup>

    <van-popup
      v-model="pptVisible"
      position="bottom"
      :overlay-style="pptOverlayStyle"
      :style="pptPopupStyle"
      class="course-material-ppt-popup-wrap"
      @opened="updatePptPopupBoundary"
      @closed="handlePptClosed"
    >
      <div class="course-material-ppt-popup">
        <van-icon name="cross" class="ppt-close-icon" @click="pptVisible = false" />
        <vue-office-pptx
          v-if="documentType === 'ppt' && pptUrl && pptOptions.width"
          :src="pptUrl"
          :options="pptOptions"
          class="ppt-viewer"
          @rendered="pptLoading = false"
          @error="handlePptRenderError"
        />
        <div
          v-if="documentType === 'pdf' && pdfUrl"
          ref="pdfViewer"
          class="pdf-viewer"
        ></div>
        <div
          v-if="documentType === 'image' && pptUrl"
          ref="imageViewer"
          class="image-viewer"
        >
          <div
            ref="imageViewerContainer"
            class="image-viewer-container"
            :class="{ 'is-zoomed': imageScale > 1 }"
          >
            <div
              class="image-viewer-stage"
              :style="imageStageStyle"
              @touchstart="handleImageTouchStart"
              @touchmove="handleImageTouchMove"
              @touchend="handleImageTouchEnd"
              @touchcancel="handleImageTouchEnd"
            >
              <img
                :src="pptUrl"
                class="image-viewer-img"
                alt=""
                draggable="false"
                @load="handleImageLoaded"
                @error="handleImageLoaded"
              />
            </div>
          </div>
        </div>
        <div v-if="pptLoading" class="ppt-loading-mask">
          <van-loading class="ppt-loading" color="#fff" size="24px" vertical>加载中...</van-loading>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import TrainingCampVideoPlayer from '@/views/RTCZone/compontents/TrainingCampVideoPlayer.vue'
import VueOfficePptx from '@vue-office/pptx'
import Pdfh5 from 'pdfh5'
import 'pdfh5/css/pdfh5.css'

const IMAGE_MIN_SCALE = 1
const IMAGE_MAX_SCALE = 5

export default {
  name: 'CourseMaterialPopup',
  components: {
    TrainingCampVideoPlayer,
    VueOfficePptx
  },
  data() {
    return {
      videoVisible: false,
      pptVisible: false,
      pptLoading: false,
      videoFullscreen: false,
      videoPopupStyle: null,
      videoOverlayStyle: null,
      pptPopupStyle: null,
      pptOverlayStyle: null,
      pptOptions: {},
      playerVideo: null,
      videoEnableTrace: false,
      pptUrl: null,
      pdfUrl: null,
      pdfh5: null,
      documentType: 'ppt',
      imageScale: 1,
      imageTranslateX: 0,
      imageTranslateY: 0,
      imageTouch: null
    }
  },
  computed: {
    imageStageStyle() {
      return {
        transform: `translate3d(${this.imageTranslateX}px, ${this.imageTranslateY}px, 0) scale(${this.imageScale})`
      }
    }
  },
  beforeDestroy() {
    this.closeAll()
    window.removeEventListener('resize', this.updateVideoPopupBoundary)
    window.removeEventListener('resize', this.updatePptPopupBoundary)
  },
  methods: {
    openVideo(playerVideo, options = {}) {
      this.videoEnableTrace = !!options.enableTrace
      this.playerVideo = playerVideo
      this.videoFullscreen = false
      this.updateVideoPopupBoundary()
      window.removeEventListener('resize', this.updateVideoPopupBoundary)
      window.addEventListener('resize', this.updateVideoPopupBoundary)
      this.videoVisible = true
      this.$nextTick(this.updateVideoPopupBoundary)
    },
    openPpt(url) {
      const previewUrl = this.resolveMaterialPreviewUrl(url)
      if (this.isPdfUrl(previewUrl)) {
        this.openPdf(previewUrl)
        return
      }
      if (this.isImageUrl(previewUrl)) {
        this.openImage(previewUrl)
        return
      }
      this.destroyPdfViewer()
      this.documentType = 'ppt'
      this.pptUrl = previewUrl
      this.pdfUrl = null
      this.pptLoading = true
      this.updatePptPopupBoundary()
      window.removeEventListener('resize', this.updatePptPopupBoundary)
      window.addEventListener('resize', this.updatePptPopupBoundary)
      this.pptVisible = true
      this.$nextTick(this.updatePptPopupBoundary)
    },
    openPdf(url) {
      const previewUrl = this.resolveMaterialPreviewUrl(url)
      this.destroyPdfViewer()
      this.documentType = 'pdf'
      this.pdfUrl = previewUrl
      this.pptUrl = null
      this.pptLoading = true
      this.updatePptPopupBoundary()
      window.removeEventListener('resize', this.updatePptPopupBoundary)
      window.addEventListener('resize', this.updatePptPopupBoundary)
      this.pptVisible = true
      this.$nextTick(() => {
        this.updatePptPopupBoundary()
        this.initPdfViewer()
      })
    },
    openImage(url) {
      const previewUrl = this.resolveMaterialPreviewUrl(url)
      this.destroyPdfViewer()
      this.documentType = 'image'
      this.pptUrl = previewUrl
      this.pdfUrl = null
      this.pptLoading = true
      this.updatePptPopupBoundary()
      window.removeEventListener('resize', this.updatePptPopupBoundary)
      window.addEventListener('resize', this.updatePptPopupBoundary)
      this.pptVisible = true
      this.$nextTick(() => {
        this.updatePptPopupBoundary()
        this.initImageViewer()
      })
    },
    initImageViewer() {
      if (this.documentType !== 'image' || !this.pptUrl || !this.$refs.imageViewer) return
      this.resetImageTransform()
    },
    resetImageTransform() {
      this.imageScale = 1
      this.imageTranslateX = 0
      this.imageTranslateY = 0
      this.imageTouch = null
      const container = this.$refs.imageViewerContainer
      if (container) {
        container.scrollLeft = 0
        container.scrollTop = 0
      }
    },
    getTouchDistance(touches) {
      const dx = touches[0].clientX - touches[1].clientX
      const dy = touches[0].clientY - touches[1].clientY
      return Math.hypot(dx, dy)
    },
    getTouchCenter(touches) {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      }
    },
    handleImageTouchStart(event) {
      if (this.documentType !== 'image') return
      const touches = event.touches
      if (touches.length === 2) {
        const container = this.$refs.imageViewerContainer
        const center = this.getTouchCenter(touches)
        let startFocalX = center.x
        let startFocalY = center.y
        if (container) {
          const rect = container.getBoundingClientRect()
          startFocalX = center.x - rect.left + container.scrollLeft
          startFocalY = center.y - rect.top + container.scrollTop
        }
        this.imageTouch = {
          mode: 'pinch',
          startDistance: this.getTouchDistance(touches),
          startScale: this.imageScale,
          startTranslateX: this.imageTranslateX,
          startTranslateY: this.imageTranslateY,
          startFocalX,
          startFocalY
        }
        return
      }
      if (touches.length === 1 && this.imageScale > 1) {
        this.imageTouch = {
          mode: 'pan',
          startX: touches[0].clientX,
          startY: touches[0].clientY,
          startTranslateX: this.imageTranslateX,
          startTranslateY: this.imageTranslateY
        }
      }
    },
    handleImageTouchMove(event) {
      if (!this.imageTouch || this.documentType !== 'image') return
      const touches = event.touches
      if (this.imageTouch.mode === 'pinch' && touches.length >= 2) {
        event.preventDefault()
        const distance = this.getTouchDistance(touches)
        if (!this.imageTouch.startDistance) return
        let nextScale = this.imageTouch.startScale * (distance / this.imageTouch.startDistance)
        nextScale = Math.min(IMAGE_MAX_SCALE, Math.max(IMAGE_MIN_SCALE, nextScale))
        const scaleRatio = nextScale / this.imageTouch.startScale
        const { startFocalX, startFocalY, startTranslateX, startTranslateY } = this.imageTouch
        this.imageTranslateX = startTranslateX + (startFocalX - startTranslateX) * (1 - scaleRatio)
        this.imageTranslateY = startTranslateY + (startFocalY - startTranslateY) * (1 - scaleRatio)
        this.imageScale = nextScale
        if (nextScale <= 1) {
          this.resetImageTransform()
        }
        return
      }
      if (this.imageTouch.mode === 'pan' && touches.length === 1 && this.imageScale > 1) {
        event.preventDefault()
        const dx = touches[0].clientX - this.imageTouch.startX
        const dy = touches[0].clientY - this.imageTouch.startY
        this.imageTranslateX = this.imageTouch.startTranslateX + dx
        this.imageTranslateY = this.imageTouch.startTranslateY + dy
      }
    },
    handleImageTouchEnd(event) {
      if (!this.imageTouch) return
      const touches = event.touches
      if (!touches.length) {
        if (this.imageScale <= 1) {
          this.resetImageTransform()
        }
        this.imageTouch = null
        return
      }
      if (touches.length === 1 && this.imageTouch.mode === 'pinch' && this.imageScale > 1) {
        this.imageTouch = {
          mode: 'pan',
          startX: touches[0].clientX,
          startY: touches[0].clientY,
          startTranslateX: this.imageTranslateX,
          startTranslateY: this.imageTranslateY
        }
      }
    },
    handleImageLoaded() {
      this.pptLoading = false
    },
    handlePptRenderError() {
      this.pptLoading = false
      if (this.$toast) {
        this.$toast('文件加载失败，请稍后重试')
      }
    },
    flushVideoTrace() {
      const mediaVideoPlayer = this.$refs.mediaVideoPlayer
      if (mediaVideoPlayer && this.videoEnableTrace && mediaVideoPlayer.handleVideoTrace) {
        mediaVideoPlayer.handleVideoTrace(mediaVideoPlayer.curVideoPlayer)
      }
    },
    closeVideo() {
      this.flushVideoTrace()
      if (this.$refs.mediaVideoPlayer) {
        this.$refs.mediaVideoPlayer.pausePlayback()
        this.$refs.mediaVideoPlayer.destroyPlayer()
      }
      this.videoVisible = false
      this.playerVideo = null
      this.videoEnableTrace = false
      this.videoFullscreen = false
      this.videoPopupStyle = null
      this.videoOverlayStyle = null
      window.removeEventListener('resize', this.updateVideoPopupBoundary)
    },
    closePpt() {
      this.pptVisible = false
      this.pptUrl = null
      this.pdfUrl = null
      this.pptLoading = false
      this.pptPopupStyle = null
      this.pptOverlayStyle = null
      this.pptOptions = {}
      this.documentType = 'ppt'
      this.resetImageTransform()
      this.destroyPdfViewer()
      window.removeEventListener('resize', this.updatePptPopupBoundary)
    },
    closeAll() {
      this.closeVideo()
      this.closePpt()
    },
    handleVideoClosed() {
      this.flushVideoTrace()
      if (this.$refs.mediaVideoPlayer) {
        this.$refs.mediaVideoPlayer.pausePlayback()
        this.$refs.mediaVideoPlayer.destroyPlayer()
      }
      this.playerVideo = null
      this.videoEnableTrace = false
      this.videoFullscreen = false
      this.videoPopupStyle = null
      this.videoOverlayStyle = null
      window.removeEventListener('resize', this.updateVideoPopupBoundary)
    },
    handleVideoFullscreenChange(isFullscreen) {
      this.videoFullscreen = isFullscreen
      this.$nextTick(this.updateVideoPopupBoundary)
    },
    getPageContainerRect() {
      const fallbackWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth || 0
      const fallbackHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight || 0
      const fallbackRect = {
        left: 0,
        top: 0,
        width: fallbackWidth,
        height: fallbackHeight
      }
      let pageContainer = this.$el
      while (pageContainer && pageContainer.nodeType === 1) {
        const classList = pageContainer.classList
        if (classList && (classList.contains('pfa-list') || classList.contains('course-detail'))) break
        pageContainer = pageContainer.parentElement
      }
      pageContainer = pageContainer || document.getElementById('app')
      if (!pageContainer || !pageContainer.getBoundingClientRect) return fallbackRect
      const rect = pageContainer.getBoundingClientRect()
      if (!rect.width) return fallbackRect
      return {
        left: rect.left,
        top: rect.top,
        width: Math.min(rect.width, fallbackWidth || rect.width),
        height: rect.height || fallbackHeight
      }
    },
    updateVideoPopupBoundary() {
      const rect = this.getPageContainerRect()
      const popupWidth = `${rect.width}px`
      const popupLeft = `${rect.left}px`
      this.videoPopupStyle = {
        '--course-material-popup-left': popupLeft,
        '--course-material-popup-center-left': `${rect.left + rect.width / 2}px`,
        '--course-material-popup-width': popupWidth
      }
      this.videoOverlayStyle = {
        left: popupLeft,
        right: 'auto',
        width: popupWidth,
        height: '100vh'
      }
    },
    updatePptPopupBoundary() {
      const rect = this.getPageContainerRect()
      const popupWidth = `${rect.width}px`
      const popupLeft = `${rect.left}px`
      const popupHeight = Math.round(window.innerHeight || document.documentElement.clientHeight || rect.height || 0)
      this.pptPopupStyle = {
        left: popupLeft,
        right: 'auto',
        width: popupWidth,
        height: `${popupHeight}px`,
        maxHeight: '100vh'
      }
      this.pptOverlayStyle = {
        left: popupLeft,
        right: 'auto',
        width: popupWidth,
        height: '100vh'
      }
      this.pptOptions = {
        width: Math.round(rect.width),
        height: popupHeight
      }
      if (this.documentType === 'image' && this.pptVisible) {
        this.$nextTick(this.initImageViewer)
      }
    },
    isPdfUrl(url) {
      if (!url) return false
      const cleanUrl = `${url}`.split('?')[0].split('#')[0]
      return /\.pdf$/i.test(cleanUrl)
    },
    isImageUrl(url) {
      if (!url) return false
      const cleanUrl = `${url}`.split('?')[0].split('#')[0]
      return /\.(png|jpe?g|gif|webp)$/i.test(cleanUrl)
    },
    resolveMaterialPreviewUrl(url) {
      if (!url) return url
      const urlStr = `${url}`.trim()
      if (process.env.NODE_ENV !== 'development') return urlStr
      if (urlStr.startsWith('/bw/')) return urlStr
      const bwIndex = urlStr.indexOf('/bw/')
      if (bwIndex > -1) return urlStr.slice(bwIndex)
      return urlStr
    },
    initPdfViewer() {
      if (this.documentType !== 'pdf' || !this.pdfUrl || !this.$refs.pdfViewer || this.pdfh5) return
      try {
        this.pdfh5 = new Pdfh5(this.$refs.pdfViewer, {
          pdfurl: this.pdfUrl
        })
        this.pdfh5.on('complete', () => {
          this.pptLoading = false
        })
        this.pdfh5.on('error', () => {
          this.pptLoading = false
          if (this.$toast) {
            this.$toast('PDF 加载失败，请稍后重试')
          }
        })
      } catch (err) {
        this.pptLoading = false
        console.log('initPdfViewer err', err)
      }
    },
    destroyPdfViewer() {
      if (this.pdfh5 && this.pdfh5.destroy) {
        this.pdfh5.destroy()
      }
      this.pdfh5 = null
    },
    handlePptClosed() {
      this.resetImageTransform()
      this.destroyPdfViewer()
      this.pptUrl = null
      this.pdfUrl = null
      this.pptLoading = false
      this.pptPopupStyle = null
      this.pptOverlayStyle = null
      this.pptOptions = {}
      this.documentType = 'ppt'
      window.removeEventListener('resize', this.updatePptPopupBoundary)
    }
  }
}
</script>

<style lang="less" scoped>
.course-material-ppt-popup {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  .ppt-close-icon {
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 10;
    font-size: 20px;
    color: #333;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    padding: 4px;
  }

  .ppt-loading-mask {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 9;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    background: #000;

    /deep/ .van-loading__text {
      color: #fff;
    }
  }

  .ppt-viewer {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }

  .pdf-viewer,
  .image-viewer {
    position: absolute;
    top: 0;
    left: 0;
    z-index: 1;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .image-viewer {
    background: #000;
  }

  .image-viewer-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: auto;
    -webkit-overflow-scrolling: touch;

    &.is-zoomed {
      overflow: hidden;
      touch-action: none;
    }
  }

  .image-viewer-stage {
    transform-origin: 0 0;
    will-change: transform;
  }

  .image-viewer-img {
    display: block;
    width: 100%;
    max-width: 100%;
    height: auto;
    margin: 0 auto;
    user-select: none;
    -webkit-user-drag: none;
  }

  .pdf-viewer {
    background: #f5f5f5;
  }
}

/deep/ .course-material-ppt-popup-wrap {
  overflow: hidden;
}

/deep/ .course-material-video-popup {
  left: var(--course-material-popup-center-left, 50%);
  width: var(--course-material-popup-width, 100%);
  background: #000;
  border-radius: 0;
  overflow: hidden;

  .player {
    width: 100%;
  }
}

/deep/ .course-material-video-popup.course-material-video-popup-fullscreen {
  position: fixed !important;
  top: 0 !important;
  left: var(--course-material-popup-left, 0px) !important;
  right: auto !important;
  bottom: 0 !important;
  width: var(--course-material-popup-width, 100vw) !important;
  height: 100vh !important;
  max-height: none !important;
  transform: none !important;
  overflow: visible !important;
  z-index: 9998 !important;
  border-radius: 0 !important;

  .player {
    width: 100%;
    height: 100vh;
    overflow: visible;
  }

  .video-js.player-isfullscreen2 {
    position: relative !important;
    top: auto !important;
    left: auto !important;
    right: auto !important;
    bottom: auto !important;
    width: 100% !important;
    height: 100vh !important;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/CourseMaterialPopup.vue